/**
 * Páginas públicas de contenido simple (Fase 3 SEO): /planes y /de-excel-a-app.
 * Las renderiza src/pages/PaginaInfoPage.tsx. Title y description en
 * seoPaginas.json (`seoKey`); preguntas frecuentes en src/content/faq/<faq>.json
 * (misma fuente para la página y el FAQPage del prerender).
 */

export interface SeccionInfo {
  titulo: string;
  parrafos?: string[];
  /** Lista con viñetas. */
  items?: string[];
  /** Lista numerada. */
  pasos?: string[];
  tabla?: { encabezados: string[]; filas: string[][] };
}

export interface PaginaInfo {
  ruta: string;
  seoKey: 'planes' | 'deExcelAApp';
  faq: 'planes' | 'de-excel-a-app';
  eyebrow: string;
  titulo: string;
  intro: string;
  cta: { label: string; to: string };
  ctaSecundario?: { label: string; to: string };
  secciones: SeccionInfo[];
  cierre: { titulo: string; texto: string };
}

export const PAGINAS_INFO: Record<'planes' | 'deExcelAApp', PaginaInfo> = {
  planes: {
    ruta: '/planes',
    seoKey: 'planes',
    faq: 'planes',
    eyebrow: 'Planes y precio',
    titulo: 'Precio del software ganadero: hoy es gratis y trae todo',
    intro:
      'Mi Campo Digital hoy tiene un solo plan y es gratis: incluye todas las funciones del software ganadero, no es una prueba por unos días y no te pide tarjeta. Creás la cuenta, cargás tu rodeo y empezás a usarlo en la manga el mismo día.',
    cta: { label: 'Creá tu cuenta gratis', to: '/login' },
    ctaSecundario: { label: 'Ver funcionalidades', to: '/funcionalidades' },
    secciones: [
      {
        titulo: 'Qué incluye el plan gratis',
        items: [
          'Registro de cada animal por caravana, con su historia completa: pesadas, tactos, movimientos y recambios de caravana.',
          'Lectura de caravana electrónica y balanza por Bluetooth en el celular o USB en la compu.',
          'Carga sin señal en la manga, con sincronización automática al volver la conexión.',
          'Preñez por lote y por vaca, ganancia diaria, genealogía y reportes.',
          'Declaración del SIGSA: el archivo listo para subir y la marca de lo que ya declaraste.',
          'Importación de tu Excel tal como está y exportación a Excel o CSV.',
          'Mapa del campo con potreros, aguadas, lluvias y pastura.',
          'Gastos, ingresos, inventario y valuación del rodeo.',
          'Agenda y recordatorios: vacunas, vencimientos y tareas del campo.',
          'Varios usuarios con roles: dueño, administrador, gerente y operador.',
          'Asistente con inteligencia artificial por texto o audio.',
        ],
      },
      {
        titulo: 'Dónde se usa',
        parrafos: [
          'Hay app para Android y además funciona en el navegador de cualquier celular, tablet o compu. En iPhone, por ahora, se usa desde el navegador: la app para iPhone todavía no está disponible.',
        ],
      },
      {
        titulo: 'Cuánto cuesta llevar el registro de otra forma',
        tabla: {
          encabezados: ['', 'Cuaderno', 'Excel', 'Mi Campo Digital'],
          filas: [
            ['Precio', 'Gratis', 'Gratis o la licencia de Office', 'Gratis'],
            ['Tiempo de carga', 'Doble: anotar y pasar', 'Tipear cada dato', 'Lector y balanza, sin tipear'],
            ['Errores de tipeo', 'Frecuentes', 'Frecuentes', 'Pocos: el número entra solo'],
            ['Números del rodeo', 'A mano', 'Con fórmulas', 'Se calculan solos'],
            ['SIGSA', 'A mano', 'A mano', 'Archivo listo'],
          ],
        },
      },
    ],
    cierre: {
      titulo: '¿Y si más adelante hay planes pagos?',
      texto:
        'Si algún día sumamos planes pagos, te vamos a avisar con anticipación y tus datos siguen siendo tuyos: los podés exportar a Excel cuando quieras.',
    },
  },
  deExcelAApp: {
    ruta: '/de-excel-a-app',
    seoKey: 'deExcelAApp',
    faq: 'de-excel-a-app',
    eyebrow: 'De Excel a la app',
    titulo: 'Software ganadero gratis que importa tu Excel tal como está',
    intro:
      'Si llevás el rodeo en una planilla de Excel, no tenés que volver a cargarlo. Subís el archivo tal como está a Mi Campo Digital, la app reconoce las columnas y en unos minutos tenés tus animales en un software ganadero gratis, listo para usar en la manga con lector de caravanas y balanza.',
    cta: { label: 'Importar mi Excel gratis', to: '/login' },
    ctaSecundario: { label: 'Bajar una planilla para empezar', to: '/recursos/planilla-ganadera' },
    secciones: [
      {
        titulo: 'Cómo se importa, paso a paso',
        pasos: [
          'Creá tu cuenta gratis y entrá a Mi Ganado.',
          'Tocá Importar y subí tu planilla en Excel (.xlsx) o CSV.',
          'La app detecta la hoja con animales y reconoce las columnas. Si alguna tiene un nombre raro, la inteligencia artificial te propone a qué dato corresponde y vos confirmás.',
          'Revisá la vista previa: si una celda tiene un dato inválido, como una fecha rara o un peso sin número, lo corregís ahí mismo.',
          'Importá. Tus animales quedan cargados con su historia y ya podés seguir desde el celular.',
        ],
      },
      {
        titulo: 'Qué entiende de tu planilla',
        items: [
          'Caravana, fecha, categoría, sexo, raza, edad por dentición, peso, preñez, ternero al pie, estado y observaciones.',
          'Palabras de campo: "vaq", "vc", "md", "BLL", "c/t", "vacía", "vendido".',
          'Varias pesadas o varios tactos en columnas, cada uno con su fecha, aunque la planilla no diga el año.',
          'Varias hojas en el mismo archivo: años o rodeos separados en una sola pasada.',
          'Exportaciones de otros sistemas, como las del SENASA, en CSV con coma o punto y coma.',
        ],
      },
      {
        titulo: 'Qué ganás al pasar del Excel a la app',
        tabla: {
          encabezados: ['', 'Excel', 'Mi Campo Digital'],
          filas: [
            [
              'En la manga',
              'Incómodo en el celular, necesita señal para compartir',
              'Pensado para la manga, funciona sin señal',
            ],
            ['Caravana y peso', 'Se tipean', 'Entran solos con el lector y la balanza'],
            ['Equipo', 'Un archivo que se pisa entre versiones', 'Cada uno con su usuario y sus permisos'],
            ['Indicadores', 'Fórmulas que se rompen', 'GDP, preñez y valor del rodeo, solos'],
            ['SIGSA', 'Se arma a mano', 'Archivo listo para subir'],
            ['Precio', 'Gratis', 'Gratis'],
          ],
        },
      },
    ],
    cierre: {
      titulo: '¿Y si después quiero volver al Excel?',
      texto: 'Exportás tus animales y tus sesiones a Excel o CSV cuando quieras. Tus datos son tuyos.',
    },
  },
};
